import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { TableRow, TableSortLabel, Typography } from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import TableCell from 'src/components/Table/TableCell';
import { useTranslation } from 'react-i18next';
import TablePaginationCustom from 'src/components/Table/TablePaginationCustom';
import Table from 'src/components/Table';
import useStyles from './styles';
import ResumeRow from './ResumeRow';

if (!process.env.REACT_APP_TABLE_SIZE) {
  console.error(
    'REACT_APP_TABLE_SIZE variable not loaded properly. Check your .env file please!'
  );
}

const Tbody = ({ metricsResume }) =>
  metricsResume.map(resume => <ResumeRow key={resume.id} {...resume} />);

const MetricsTable = ({
  className,
  metricsResume,
  count,
  page,
  handleChangePage,
  loading,
  onSortChange,
  ...rest
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');

  const handleRequestSort = useCallback(
    (_event, property) => {
      const isDesc = property === orderBy && order === 'desc';
      setOrder(isDesc ? 'asc' : 'desc');
      setOrderBy(property);
      onSortChange(property, isDesc ? 'asc' : 'desc');
    },
    [order, orderBy, setOrder, setOrderBy, onSortChange]
  );

  const createSortHandler = useCallback(
    property => event => {
      handleRequestSort(event, property);
    },
    [handleRequestSort]
  );

  const theads = useMemo(
    () => () => (
      <TableRow>
        <TableCell
          variant="head"
          key="id"
          sortDirection={orderBy === 'date' ? order : false}
        >
          <TableSortLabel
            active={orderBy === 'date'}
            direction={orderBy === 'date' ? order : 'desc'}
            onClick={createSortHandler('date')}
            hideSortIcon={orderBy !== 'date'}
          >
            <Typography variant="head" color="textSecondary" noWrap>
              {t('COMMON.MONTH')}
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell variant="head" key="quotes_in_review" align="center">
          {t('BUSINESS_VIEW.RESUME.IN_PROGRESS')}
        </TableCell>
        <TableCell
          variant="head"
          key="quotes_in_review"
          align="center"
          sortDirection={orderBy === 'quotesInReview' ? order : false}
        >
          <TableSortLabel
            active={orderBy === 'quotesInReview'}
            direction={orderBy === 'quotesInReview' ? order : 'desc'}
            onClick={createSortHandler('quotesInReview')}
            hideSortIcon={orderBy !== 'quotesInReview'}
          >
            <Typography variant="head" color="textSecondary" noWrap>
              {t('BUSINESS_VIEW.RESUME.IN_REVIEW')}
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell
          variant="head"
          key="quotes_in_draft"
          align="center"
          sortDirection={orderBy === 'quotesInDraft' ? order : false}
        >
          <TableSortLabel
            active={orderBy === 'quotesInDraft'}
            direction={orderBy === 'quotesInDraft' ? order : 'desc'}
            onClick={createSortHandler('quotesInDraft')}
            hideSortIcon={orderBy !== 'quotesInDraft'}
          >
            <Typography variant="head" color="textSecondary" noWrap>
              {t('BUSINESS_VIEW.RESUME.IN_DRAFT')}
            </Typography>
          </TableSortLabel>
        </TableCell>
        <TableCell variant="head" key="quotes-accepted" align="center">
          {t('BUSINESS_VIEW.RESUME.ACCEPTED')}
        </TableCell>
        <TableCell variant="head" key="quotes_rejected" align="center">
          {t('BUSINESS_VIEW.RESUME.REJECTED')}
        </TableCell>
        <TableCell variant="head" key="quotes_archived" align="center">
          {t('BUSINESS_VIEW.RESUME.ARCHIVED')}
        </TableCell>
      </TableRow>
    ),
    [classes, t, order, orderBy, createSortHandler]
  );

  const tbody = useMemo(() => () => <Tbody metricsResume={metricsResume} />, [
    metricsResume
  ]);

  const pagination = useMemo(
    () => () => (
      <TablePagination
        rowsPerPageOptions={[]}
        component="div"
        count={count}
        rowsPerPage={+process.env.REACT_APP_TABLE_SIZE}
        page={page}
        onPageChange={handleChangePage}
        ActionsComponent={TablePaginationCustom}
        labelDisplayedRows={({ from, to, count: c }) => {
          return `${from}-${to} ${t('COMMON.OF')} ${c}`;
        }}
      />
    ),
    [page, metricsResume]
  );

  return (
    <Table
      {...rest}
      variant="outlined"
      tbody={tbody}
      theads={theads}
      pagination={pagination}
      loading={loading}
    />
  );
};

MetricsTable.propTypes = {
  className: PropTypes.string,
  metricsResume: PropTypes.array.isRequired,
  count: PropTypes.number,
  page: PropTypes.number,
  handleChangePage: PropTypes.func,
  loading: PropTypes.bool,
  onSortChange: PropTypes.func
};

MetricsTable.defaultProps = {
  count: 0
};

export default MetricsTable;
